// @greenlight-spec: 11-observability.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AppReservationsSchema, CapacityChangeSchema, CapacityPostureSchema, DeclineCapacityAskSchema, EgressReachabilitySchema, RequestCapacityChangeResponseSchema, RequestCapacityChangeSchema, FleetContactDeliverySchema, FleetEnrollmentSchema, FleetKeyRotationSchema, FleetReportDeliverySchema, FleetEntitlementSignalSchema, FleetReleaseValidationSwitchSchema, FleetReportingEnforcementSchema, HealthFreshnessSchema, HealthIncidentDetailSchema, HealthIncidentSchema, HealthIncidentsQuerySchema, HealthSeriesQuerySchema, HealthSeriesSchema, HealthSnapshotSchema, HealthWorkerTaskFailureLogSchema, HealthWorkerTasksSchema, PaginationQuerySchema, paginated, ReleaseValidationStatusSchema, RerunReleaseValidationRequestSchema, RerunReleaseValidationResponseSchema, TriggerUpdaterRunRequestSchema, TriggerUpdaterRunResponseSchema, UpdateRunDetailSchema, UpdateRunLogPageSchema, UpdateRunSummarySchema, UpdaterPostureSchema, UpdaterRunsQuerySchema, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const RunIdParamsSchema = z.object({ id: z.string().uuid() });
const c = initContract();
// Internal-health endpoints: snapshot and fleet-report reads, the updater
// posture/run explorer, plus the one narrowly authorized write docs/26 defers
// dashboard-triggered execution to. Nullable snapshot/fleet payloads mean
// "no row yet" (fresh install, job hasn't run). The runs list and transcript
// paginate per docs/16; the other reads are single-resource.
export const healthContract = c.router({
    latestSnapshot: {
        method: 'GET',
        path: '/api/health/snapshot',
        responses: {
            200: z.object({
                snapshot: HealthSnapshotSchema.nullable(),
                freshness: HealthFreshnessSchema,
            }),
            ...errorResponses,
        },
        summary: 'Return the most recent control-plane health snapshot (org-admin scoped).',
    },
    appReservations: {
        method: 'GET',
        path: '/api/health/app-reservations',
        responses: {
            200: AppReservationsSchema,
            ...errorResponses,
        },
        summary: 'Rank running apps by the app-node capacity they reserve but do not use (org-admin scoped).',
    },
    series: {
        method: 'GET',
        path: '/api/health/series',
        query: HealthSeriesQuerySchema,
        responses: {
            200: HealthSeriesSchema,
            ...errorResponses,
        },
        summary: "Return one control-plane metric's history over a window (org-admin scoped).",
    },
    incidents: {
        method: 'GET',
        path: '/api/health/incidents',
        query: HealthIncidentsQuerySchema,
        responses: {
            200: paginated(HealthIncidentSchema),
            ...errorResponses,
        },
        summary: 'List observed control-plane incidents in a date range, newest first (org-admin scoped).',
    },
    incident: {
        method: 'GET',
        path: '/api/health/incidents/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: HealthIncidentDetailSchema,
            ...errorResponses,
        },
        summary: 'Return one observed control-plane incident and the state of its log capture (org-admin scoped).',
    },
    incidentDiagnostics: {
        method: 'GET',
        path: '/api/health/incidents/:id/diagnostics',
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: c.otherResponse({ contentType: 'text/plain; charset=utf-8', body: c.type() }),
            ...errorResponses,
        },
        summary: "Download an incident's captured, redacted log tail as an inert text attachment (org-admin only, audited).",
    },
    workerTasks: {
        method: 'GET',
        path: '/api/health/worker-tasks',
        responses: {
            200: HealthWorkerTasksSchema,
            ...errorResponses,
        },
        summary: 'List the scheduled background jobs that are failing or overdue, under readable labels (org-admin scoped).',
    },
    workerTaskFailureLog: {
        method: 'GET',
        path: '/api/health/worker-task-failure-logs/:id',
        pathParams: z.object({ id: z.string().uuid() }),
        responses: {
            200: HealthWorkerTaskFailureLogSchema,
            ...errorResponses,
        },
        summary: "Return a scheduled background job's latest failed run log, redacted (org-admin only, audited).",
    },
    triggerUpdaterRun: {
        method: 'POST',
        path: '/api/health/updater/runs',
        body: TriggerUpdaterRunRequestSchema,
        responses: {
            // 202: the Job exists, the run does not yet — the updater opens its own
            // record when the pod starts (docs/26 §Status reporting).
            202: TriggerUpdaterRunResponseSchema,
            ...errorResponses,
        },
        summary: 'Start a manual updater run — canary preview or apply (org-admin only).',
    },
    releaseValidationStatus: {
        method: 'GET',
        path: '/api/health/release-validation',
        responses: { 200: ReleaseValidationStatusSchema, ...errorResponses },
        summary: "Return the running build's release-validation verdict per suite (org-admin only).",
    },
    rerunReleaseValidation: {
        method: 'POST',
        path: '/api/health/release-validation/rerun',
        body: RerunReleaseValidationRequestSchema,
        responses: {
            // 202: the worker has been asked; the rows reset when it services the request.
            202: RerunReleaseValidationResponseSchema,
            ...errorResponses,
        },
        summary: 'Ask the worker to validate the running build again (org-admin only, audited).',
    },
    capacityPosture: {
        method: 'GET',
        path: '/api/health/capacity',
        responses: {
            200: CapacityPostureSchema,
            ...errorResponses,
        },
        summary: 'Return the effective node counts, how each capacity input may change, the open and recent changes, and any ask or shrink suggestion (org-admin only).',
    },
    requestCapacityChange: {
        method: 'POST',
        path: '/api/health/capacity/changes',
        body: RequestCapacityChangeSchema,
        responses: {
            // 202: recorded; an updater run applies it — now, or at the next window.
            202: RequestCapacityChangeResponseSchema,
            ...errorResponses,
        },
        summary: 'Record a change to the app-pool bounds or system node count for the updater to apply (org-admin only).',
    },
    declineCapacityAsk: {
        method: 'POST',
        path: '/api/health/capacity/ask/decline',
        body: DeclineCapacityAskSchema,
        responses: {
            201: CapacityChangeSchema,
            ...errorResponses,
        },
        summary: "Decline the ask to raise the app pool's ceiling, and record the refusal (org-admin only).",
    },
    egressReachability: {
        method: 'GET',
        path: '/api/health/egress',
        responses: {
            200: EgressReachabilitySchema,
            ...errorResponses,
        },
        summary: "Return whether the Egress section applies, and the last check of each destination the customer's firewall must allow (org-admin only).",
    },
    checkEgressReachability: {
        method: 'POST',
        path: '/api/health/egress/checks',
        body: c.noBody(),
        responses: {
            // A request within a minute of the last check returns its state rather than starting another.
            200: EgressReachabilitySchema,
            ...errorResponses,
        },
        summary: 'Start a check of every egress destination, at most one per minute per install (org-admin only).',
    },
    latestFleetReport: {
        method: 'GET',
        path: '/api/health/fleet-report',
        responses: {
            200: z.object({
                // Null until the forwarder's first tick (reporting is always on;
                // there is no disabled state).
                enrollment: FleetEnrollmentSchema.nullable(),
                delivery: FleetReportDeliverySchema.nullable(),
                // Null until the first install-initiated key-rotation attempt.
                rotation: FleetKeyRotationSchema.nullable(),
                // Null until the wizard's contact step has named somebody to forward.
                contact: FleetContactDeliverySchema.nullable(),
                // Always present: an install with no evaluated state yet reads as `ok`.
                enforcement: FleetReportingEnforcementSchema,
                // What Shift last asserted about this install's license on a report
                // response (docs/27). Null until the first delivery carrying one — an
                // install whose fleet monitor predates the field never gets one.
                entitlement: FleetEntitlementSignalSchema.nullable(),
                // Whether this install validates releases for Shift: Shift's last
                // `gates_releases` answer and the install's own prerelease posture.
                // Always present; reads as off until Shift has answered (docs/26).
                release_validation: FleetReleaseValidationSwitchSchema,
            }),
            ...errorResponses,
        },
        summary: 'Return the fleet-monitor enrollment state, the latest outbound Fleet Health Report delivery, the install-contact forward, and the reporting-enforcement state (org-admin scoped).',
    },
    // The forward-looking half of updater state: whether the install updates
    // itself, when it will next try, and whether it is behind. Read live rather
    // than collected into the health snapshot, because "next scheduled run" goes
    // stale between collection ticks and because the snapshot is forwarded to
    // Shift, which the install's schedule is not.
    updaterPosture: {
        method: 'GET',
        path: '/api/health/updater',
        responses: {
            200: UpdaterPostureSchema,
            ...errorResponses,
        },
        summary: 'Return the updater posture: enablement, schedule, next run, and release currency.',
    },
    updaterRuns: {
        method: 'GET',
        path: '/api/health/updater/runs',
        query: UpdaterRunsQuerySchema,
        responses: {
            200: paginated(UpdateRunSummarySchema),
            ...errorResponses,
        },
        summary: 'List retained updater runs, newest first, optionally excluding runs that found nothing to apply (org-admin scoped).',
    },
    updaterRun: {
        method: 'GET',
        path: '/api/health/updater/runs/:id',
        pathParams: RunIdParamsSchema,
        responses: {
            200: UpdateRunDetailSchema,
            ...errorResponses,
        },
        summary: 'Return one updater run: phase timeline, replayed parameters, and transcript metadata.',
    },
    updaterRunLog: {
        method: 'GET',
        path: '/api/health/updater/runs/:id/log',
        pathParams: RunIdParamsSchema,
        query: PaginationQuerySchema,
        responses: {
            200: UpdateRunLogPageSchema,
            ...errorResponses,
        },
        summary: "Return a page of one run's stored transcript as inert text (org-admin scoped).",
    },
});
//# sourceMappingURL=health.js.map